import React, { useMemo } from "react";

const ArrowUpRight = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path
            d="M7 17 17 7M9 7h8v8"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const SectionEyebrow = ({ children }) => (
    <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[.18em] text-[#8B1E3F]">
        <span className="h-px w-10 bg-current" />
        {children}
    </div>
);
const ArrowIcon = () => <ArrowUpRight className="h-4 w-4" />;
const AnimatedCounter = ({ value, suffix = "" }) => (
    <>
        {value}
        {suffix}
    </>
);
const Counter = ({ value, suffix = "" }) => (
    <>
        {value}
        {suffix}
    </>
);

const managementPlacementStats = [
    { value: 45, suffix: " LPA", label: "Highest offer" },
    { value: 25, suffix: " LPA", label: "Average offer" },
    { value: 100, suffix: "%", label: "Career assistance" },
    { value: 6, suffix: "+", label: "Career sectors" },
];
const managementCareerJourney = [
    {
        number: "01",
        title: "Discover",
        text: "Identify strengths, interests and suitable professional directions.",
    },
    {
        number: "02",
        title: "Develop",
        text: "Build communication, aptitude and role-relevant capability.",
    },
    {
        number: "03",
        title: "Experience",
        text: "Gain exposure through internships, projects and industry interaction.",
    },
    {
        number: "04",
        title: "Prepare",
        text: "Strengthen resumes, interviews and selection readiness.",
    },
];
const engineeringPlacementStats = [
    { value: "45 LPA", label: "Highest offer" },
    { value: "25 LPA", label: "Average offer" },
    { value: "100%", label: "Career assistance" },
    { value: "6+", label: "Engineering domains" },
];
const engineeringCareerServices = [
    {
        number: "01",
        tag: "Direction",
        title: "Career mapping",
        description:
            "Identify roles aligned with discipline, strengths and aspirations.",
    },
    {
        number: "02",
        tag: "Capability",
        title: "Technical preparation",
        description:
            "Strengthen aptitude, tools and discipline-specific knowledge.",
    },
    {
        number: "03",
        tag: "Evidence",
        title: "Portfolio development",
        description: "Document projects, prototypes and technical achievements.",
    },
    {
        number: "04",
        tag: "Exposure",
        title: "Industry engagement",
        description:
            "Learn through expert sessions, visits and collaborative briefs.",
    },
    {
        number: "05",
        tag: "Experience",
        title: "Internship support",
        description: "Prepare for supervised professional learning opportunities.",
    },
    {
        number: "06",
        tag: "Launch",
        title: "Recruitment readiness",
        description: "Practise resumes, interviews and professional communication.",
    },
];
const engineeringCareerPathways2 = [
    {
        code: "CE",
        title: "Civil & Infrastructure",
        roles: ["Site engineering", "Structural support", "Project coordination"],
    },
    {
        code: "ME",
        title: "Mechanical & Manufacturing",
        roles: ["Design engineering", "Production", "Quality systems"],
    },
    {
        code: "EE",
        title: "Electrical & Electronics",
        roles: ["Power systems", "Automation", "Embedded systems"],
    },
    {
        code: "CT",
        title: "Computing & Emerging Tech",
        roles: ["Software", "AI and data", "Cloud systems"],
    },
];
const itPlacementStats = [
    {
        value: "45",
        suffix: " LPA",
        label: "Highest offer",
        note: "University-wide indicator",
    },
    {
        value: "25",
        suffix: " LPA",
        label: "Average offer",
        note: "Across placement activity",
    },
    {
        value: "100",
        suffix: "%",
        label: "Career assistance",
        note: "Structured student support",
    },
    {
        value: "6",
        suffix: "+",
        label: "Digital domains",
        note: "Representative pathways",
    },
];
const itCareerServices = [
    {
        number: "01",
        title: "Career mapping",
        text: "Match strengths with evolving digital roles.",
    },
    {
        number: "02",
        title: "Portfolio engineering",
        text: "Present credible projects and technical evidence.",
    },
    {
        number: "03",
        title: "Certification guidance",
        text: "Select relevant industry learning pathways.",
    },
    {
        number: "04",
        title: "Recruitment preparation",
        text: "Practise aptitude, interviews and communication.",
    },
];
function ManagementCareerServicesPlacements({ recruiters = [] }) {
    const placementStats = managementPlacementStats,
        careerJourney = managementCareerJourney,
        statsRef = null,
        animateStats = true;
    return (
        <>
            {/* Career Services & Placements */}
            <section className="cps" aria-labelledby="career-placement-title">
                <div className="cps__hero">
                    <div className="cps__container cps__hero-grid">
                        <div className="cps__heading">
                            <span className="cps__eyebrow">Career services & placements</span>
                            <h2 id="career-placement-title">
                                From classroom capability to
                                <em> career confidence.</em>
                            </h2>
                            <p>
                                Our Career Services team works with students throughout their
                                academic journey—helping them understand their strengths, build
                                professional skills, gain industry exposure and approach
                                opportunities with confidence.
                            </p>

                            <div className="cps__actions">
                                <a
                                    href="/placements"
                                    className="cps__button cps__button--primary"
                                >
                                    Explore placements <span>↗</span>
                                </a>
                                <a
                                    href="/corporate-relations"
                                    className="cps__button cps__button--text"
                                >
                                    Partner with us <span>→</span>
                                </a>
                            </div>
                        </div>

                        <div className="cps__salary-panel">
                            <span className="cps__salary-label">Placement highlight</span>
                            <div className="cps__salary">
                                <strong>45</strong>
                                <span>
                                    <b>LPA</b>
                                    Highest offer
                                </span>
                            </div>
                            <div className="cps__salary-line">
                                <span>
                                    <small>25 LPA</small>
                                    Average offer
                                </span>
                                <span>
                                    <small>Multi-sector</small>
                                    Opportunities
                                </span>
                            </div>
                            <p>
                                Career opportunities across management, technology, banking,
                                consulting, operations and emerging business functions.
                            </p>
                        </div>
                    </div>

                    <div className="cps__container">
                        <div className="cps__stats" ref={statsRef}>
                            {placementStats.map((stat) => (
                                <div className="cps__stat" key={stat.label}>
                                    <strong>
                                        <Counter
                                            value={stat.value}
                                            suffix={stat.suffix}
                                            run={animateStats}
                                        />
                                    </strong>
                                    <span>{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="cps__journey-section">
                    <div className="cps__container">
                        <div className="cps__section-head">
                            <div>
                                <span className="cps__eyebrow">The placement pathway</span>
                                <h2>Career preparation begins before the interview.</h2>
                            </div>
                            <p>
                                A structured progression develops self-awareness, workplace
                                exposure and selection readiness across every stage of the
                                student experience.
                            </p>
                        </div>

                        <div className="cps__journey">
                            <div className="cps__journey-line" aria-hidden="true" />
                            {careerJourney.map((step) => (
                                <article className="cps__journey-step" key={step.number}>
                                    <span className="cps__step-number">{step.number}</span>
                                    <div className="cps__step-dot" aria-hidden="true" />
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </article>
                            ))}
                        </div>

                        <div className="cps__services">
                            <div className="cps__services-title">
                                <span>Career preparation studio</span>
                                <h3>Support designed around the student.</h3>
                            </div>

                            <div className="cps__service-list">
                                {[
                                    ["CV", "Resume & LinkedIn clinics"],
                                    ["GD", "Group discussion practice"],
                                    ["PI", "Mock interviews"],
                                    ["AT", "Aptitude training"],
                                    ["IN", "Internship assistance"],
                                    ["AL", "Alumni mentoring"],
                                ].map(([icon, title]) => (
                                    <div className="cps__service" key={icon}>
                                        <span>{icon}</span>
                                        <strong>{title}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="cps__recruiters" aria-label="Recruiter network">
                    <div className="cps__container cps__recruiters-head">
                        <span>Recruiter network</span>
                        <p>
                            Organisations representing diverse industries and career paths.
                        </p>
                    </div>

                    <div className="cps__ticker">
                        <div className="cps__ticker-track">
                            {[...recruiters, ...recruiters].map((recruiter, index) => (
                                <img key={`${recruiter}-${index}`} src={recruiter} alt={`${recruiter}-${index}`}/>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="cps__cta">
                    <div className="cps__container cps__cta-grid">
                        <div>
                            <span className="cps__eyebrow cps__eyebrow--gold">
                                Ready for what comes next?
                            </span>
                            <h2>Build the skills. Meet the industry. Begin your journey.</h2>
                        </div>
                        <div className="cps__cta-actions">
                            <a href="/apply-online">
                                Apply to ISBM University <span>↗</span>
                            </a>
                            <a href="mailto:placements@isbmuniversity.edu.in">
                                Contact career services <span>→</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
function EngineeringTechnologyCareerServicesPlacements() {
    return (
        <>
            {/* Career Services & Placements */}
            <section
                className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-32"
                aria-labelledby="engineering-careers-heading"
            >
                {/* Background */}
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -left-52 top-16 h-[540px] w-[540px] rounded-full bg-[#8B1E3F]/[0.055] blur-[120px]" />

                    <div className="absolute -bottom-52 right-[-10%] h-[600px] w-[600px] rounded-full bg-[#D5AD57]/15 blur-[130px]" />

                    <svg
                        viewBox="0 0 1500 800"
                        fill="none"
                        className="absolute inset-0 h-full w-full opacity-[0.03]"
                        aria-hidden="true"
                    >
                        <path
                            d="M0 160h250l100 100h260l130-130h310l110 110h340"
                            stroke="#071A2F"
                            strokeWidth="2"
                        />
                        <path
                            d="M0 650h260l100-100h280l100 100h300l120-120h340"
                            stroke="#8B1E3F"
                            strokeWidth="2"
                        />
                        <circle cx="250" cy="160" r="8" fill="#D5AD57" />
                        <circle cx="610" cy="260" r="8" fill="#D5AD57" />
                        <circle cx="1050" cy="130" r="8" fill="#D5AD57" />
                        <circle cx="360" cy="550" r="8" fill="#8B1E3F" />
                        <circle cx="740" cy="650" r="8" fill="#8B1E3F" />
                    </svg>
                </div>

                <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                    {/* Heading */}
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.6fr] lg:items-end">
                        <div>
                            <div className="flex items-center gap-4">
                                <span className="h-px w-10 bg-[#8B1E3F]" />

                                <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#8B1E3F]">
                                    Career services &amp; placements
                                </span>
                            </div>

                            <h2
                                id="engineering-careers-heading"
                                className="mt-6 max-w-4xl text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                            >
                                Engineering talent prepared for
                                <span className="italic text-[#8B1E3F]">
                                    {" "}
                                    professional impact.
                                </span>
                            </h2>
                        </div>

                        <p className="max-w-xl text-base leading-8 text-[#132337]/65 lg:pb-2">
                            A structured career-development ecosystem connects technical
                            learning with industry exposure, professional preparation and
                            emerging engineering opportunities.
                        </p>
                    </div>

                    {/* Placement dashboard */}
                    <div className="mt-14 overflow-hidden rounded-[34px] bg-[#071A2F] shadow-[0_35px_100px_rgba(7,26,47,0.20)]">
                        <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
                            {/* Dashboard introduction */}
                            <div className="relative overflow-hidden border-b border-white/10 p-7 text-white sm:p-10 lg:border-b-0 lg:border-r lg:p-12">
                                <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#8B1E3F]/35 blur-3xl" />
                                <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#D5AD57]/15 blur-3xl" />

                                <div className="relative">
                                    <span className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                        <span className="relative flex h-2 w-2">
                                            <span className="absolute h-full w-full animate-ping rounded-full bg-[#D5AD57] opacity-70 motion-reduce:animate-none" />
                                            <span className="relative h-2 w-2 rounded-full bg-[#D5AD57]" />
                                        </span>
                                        Career launch system
                                    </span>

                                    <h3
                                        className="mt-8 text-4xl font-normal leading-tight sm:text-5xl"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        From engineering education to
                                        <span className="block italic text-[#D5AD57]">
                                            career direction.
                                        </span>
                                    </h3>

                                    <p className="mt-6 text-sm leading-7 text-white/60 sm:text-base sm:leading-8">
                                        Students receive support throughout their academic
                                        journey—from identifying a career pathway to preparing for
                                        professional opportunities.
                                    </p>

                                    <div className="mt-9 space-y-3">
                                        {[
                                            "Discipline-specific career guidance",
                                            "Technical and aptitude preparation",
                                            "Resume, portfolio and interview support",
                                            "Internship and recruitment opportunities",
                                        ].map((item, index) => (
                                            <div key={item} className="flex items-center gap-4">
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#D5AD57]/30 text-[9px] font-bold text-[#D5AD57]">
                                                    0{index + 1}
                                                </span>

                                                <span className="text-sm text-white/70">{item}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <a
                                        href="/placements"
                                        className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#D5AD57] px-7 py-4 text-sm font-bold text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white"
                                    >
                                        Explore placements
                                        <span aria-hidden="true">→</span>
                                    </a>
                                </div>
                            </div>

                            {/* Placement statistics */}
                            <div className="grid grid-cols-2">
                                {engineeringPlacementStats.map((stat, index) => (
                                    <div
                                        key={stat.label}
                                        className={`group relative flex min-h-[235px] flex-col justify-end p-6
                        transition duration-300 hover:bg-white/[0.055] sm:p-8 lg:p-10 ${index % 2 === 0 ? "border-r border-white/10" : ""
                                            } ${index < 2 ? "border-b border-white/10" : ""}`}
                                    >
                                        <span className="absolute right-5 top-5 text-xs font-bold text-white/15">
                                            0{index + 1}
                                        </span>

                                        <div className="mb-auto flex items-center gap-2">
                                            <span className="h-2 w-2 rounded-full bg-[#D5AD57]" />
                                            <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/35">
                                                Placement indicator
                                            </span>
                                        </div>

                                        <strong
                                            className="text-[clamp(44px,9vw,88px)] font-normal leading-none text-[#D5AD57]"
                                            style={{
                                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                                            }}
                                        >
                                            {stat.value}
                                        </strong>

                                        <span className="mt-4 text-xs font-semibold leading-5 text-white/65 sm:text-sm">
                                            {stat.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border-t border-white/10 px-6 py-4 text-center">
                            <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">
                                University-wide placement indicators. Programme-specific
                                outcomes may vary.
                            </p>
                        </div>
                    </div>

                    {/* Career preparation sequence */}
                    <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {engineeringCareerServices.map((service) => (
                            <article
                                key={service.number}
                                className="group relative min-h-[285px] overflow-hidden rounded-[26px] border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:bg-white hover:shadow-[0_24px_60px_rgba(7,26,47,0.11)] sm:p-7"
                            >
                                <span
                                    className="pointer-events-none absolute -right-2 -top-8 select-none text-[105px] font-normal leading-none text-[#071A2F]/[0.035]"
                                    style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}
                                >
                                    {service.number}
                                </span>

                                <div className="relative flex items-center justify-between">
                                    <span className="text-xs font-bold text-[#8B1E3F]">
                                        {service.number}
                                    </span>

                                    <span className="rounded-full bg-[#8B1E3F]/10 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                        {service.tag}
                                    </span>
                                </div>

                                <h3
                                    className="relative mt-8 text-2xl font-normal text-[#071A2F]"
                                    style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}
                                >
                                    {service.title}
                                </h3>

                                <p className="relative mt-4 text-sm leading-7 text-[#132337]/60">
                                    {service.description}
                                </p>

                                <span className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-[#8B1E3F] to-[#D5AD57] transition-all duration-500 group-hover:w-full" />
                            </article>
                        ))}
                    </div>

                    {/* Engineering career pathways */}
                    <div className="mt-14">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                    Engineering career pathways
                                </span>

                                <h3
                                    className="mt-3 text-3xl font-normal text-[#071A2F] sm:text-4xl"
                                    style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}
                                >
                                    Multiple disciplines. Expanding possibilities.
                                </h3>
                            </div>

                            <span className="text-xs text-[#132337]/45">
                                Representative career directions
                            </span>
                        </div>

                        <div className="mt-8 grid gap-px overflow-hidden rounded-[26px] border border-[#071A2F]/10 bg-[#071A2F]/10 md:grid-cols-2 xl:grid-cols-4">
                            {engineeringCareerPathways2.map((pathway) => (
                                <article
                                    key={pathway.code}
                                    className="group bg-white p-6 transition duration-300 hover:bg-[#071A2F] sm:p-7"
                                >
                                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#071A2F] text-xs font-bold text-[#D5AD57] transition group-hover:bg-[#D5AD57] group-hover:text-[#071A2F]">
                                        {pathway.code}
                                    </span>

                                    <h4
                                        className="mt-6 text-xl font-normal text-[#071A2F] group-hover:text-white"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        {pathway.title}
                                    </h4>

                                    <div className="mt-5 space-y-3">
                                        {pathway.roles.map((role) => (
                                            <div key={role} className="flex items-center gap-3">
                                                <span className="h-1.5 w-1.5 rounded-full bg-[#8B1E3F] group-hover:bg-[#D5AD57]" />

                                                <span className="text-xs text-[#132337]/55 group-hover:text-white/55">
                                                    {role}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
function InformationTechnologyCareerServicesPlacements({ recruiters = [] }) {
    const placementStats = itPlacementStats,
        careerServices = itCareerServices;
    return (
        <>
            {/* CAREER SERVICES & PLACEMENTS */}
            <section
                className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-32"
                aria-labelledby="it-careers-heading"
            >
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -right-44 top-20 h-[500px] w-[500px] rounded-full bg-[#D5AD57]/10 blur-[110px]" />

                    <svg
                        viewBox="0 0 700 600"
                        className="absolute right-0 top-0 h-full w-1/2 text-[#071A2F] opacity-[0.035]"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d="M40 100h140l80 80h160l80-80h160M180 100v-70M420 180v110M500 100v-80M0 430h190l70-70h190l100 100h150"
                            stroke="currentColor"
                            strokeWidth="2"
                        />
                        <circle cx="180" cy="100" r="7" fill="currentColor" />
                        <circle cx="420" cy="180" r="7" fill="currentColor" />
                        <circle cx="260" cy="360" r="7" fill="currentColor" />
                        <circle cx="550" cy="460" r="7" fill="currentColor" />
                    </svg>
                </div>

                <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.65fr] lg:items-end">
                        <div>
                            <SectionEyebrow>Career services & placements</SectionEyebrow>

                            <h2
                                id="it-careers-heading"
                                className="mt-6 max-w-4xl text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                            >
                                From technical ability to
                                <span className="italic text-[#8B1E3F]">
                                    {" "}
                                    professional impact.
                                </span>
                            </h2>
                        </div>

                        <p className="max-w-xl text-base leading-8 text-[#132337]/65 lg:pb-2">
                            A connected career ecosystem helps students translate academic
                            learning into portfolios, internships, professional confidence and
                            future-ready technology careers.
                        </p>
                    </div>

                    {/* Placement dashboard */}
                    <div className="mt-14 overflow-hidden rounded-[32px] bg-[#071A2F] shadow-[0_35px_100px_rgba(7,26,47,0.20)]">
                        <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
                            <div className="relative overflow-hidden border-b border-white/10 p-7 text-white sm:p-10 lg:border-b-0 lg:border-r lg:p-12">
                                <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#8B1E3F]/30 blur-3xl" />
                                <div className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-[#D5AD57]/15 blur-3xl" />

                                <div className="relative">
                                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#D5AD57]" />
                                        Career intelligence
                                    </span>

                                    <h3
                                        className="mt-8 text-4xl font-normal leading-tight sm:text-5xl"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        Your career is developed
                                        <span className="block italic text-[#D5AD57]">
                                            from day one.
                                        </span>
                                    </h3>

                                    <p className="mt-6 max-w-md text-sm leading-7 text-white/60 sm:text-base sm:leading-8">
                                        Career preparation is integrated through skill mapping,
                                        mentoring, projects, certifications, internships and
                                        industry engagement.
                                    </p>

                                    <a
                                        href="/placements"
                                        className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#D5AD57] px-7 py-4 text-sm font-bold text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white"
                                    >
                                        Explore placements
                                        <ArrowIcon />
                                    </a>
                                </div>
                            </div>

                            <div className="grid grid-cols-2">
                                {placementStats.map((stat, index) => (
                                    <div
                                        key={stat.label}
                                        className={`group relative min-h-[220px] p-6 transition duration-300
                        hover:bg-white/[0.06] sm:p-8 ${index % 2 === 0 ? "border-r border-white/10" : ""} ${index < 2 ? "border-b border-white/10" : ""
                                            }`}
                                    >
                                        <span className="absolute right-5 top-4 text-xs font-bold text-white/15">
                                            0{index + 1}
                                        </span>

                                        <strong
                                            className="block text-[clamp(34px,4vw,56px)] font-normal text-[#D5AD57]"
                                            style={{
                                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                                            }}
                                        >
                                            <AnimatedCounter
                                                value={stat.value}
                                                suffix={stat.suffix}
                                            />
                                        </strong>

                                        <h3 className="mt-5 text-sm font-semibold text-white sm:text-base">
                                            {stat.label}
                                        </h3>

                                        <p className="mt-2 text-xs leading-5 text-white/45 sm:text-sm">
                                            {stat.note}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Career development journey */}
                    <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                        {careerServices.map((service) => (
                            <article
                                key={service.number}
                                className="group relative overflow-hidden rounded-[24px] border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:bg-white hover:shadow-[0_24px_60px_rgba(7,26,47,0.11)]"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-[#8B1E3F]">
                                        {service.number}
                                    </span>
                                    <span className="h-2 w-2 rounded-full bg-[#D5AD57] transition duration-300 group-hover:scale-[2]" />
                                </div>

                                <h3
                                    className="mt-8 text-2xl font-normal text-[#071A2F]"
                                    style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}
                                >
                                    {service.title}
                                </h3>

                                <p className="mt-4 text-sm leading-7 text-[#132337]/60">
                                    {service.text}
                                </p>
                            </article>
                        ))}
                    </div>

                    {/* Recruiters */}
                    <div className="mt-14 border-t border-[#071A2F]/10 pt-9">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                    Technology ecosystem
                                </span>
                                <h3 className="mt-2 text-xl font-semibold text-[#071A2F]">
                                    Organisations shaping global technology careers
                                </h3>
                            </div>

                            <span className="text-xs text-[#132337]/50">
                                Representative industry ecosystem
                            </span>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                            {recruiters.map((recruiter) => (
                                <div
                                    key={recruiter}
                                    className="group flex h-[82px] items-center justify-center rounded-2xl border border-[#071A2F]/10 bg-white px-5 transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57] hover:shadow-lg"
                                >
                                    <img
                                        src={recruiter}
                                        alt={`${recruiter} logo`}
                                        loading="lazy"
                                        className="max-h-10 w-auto max-w-full object-contain transition duration-300"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
const buildCareerStages = ({
    discipline,
    skills,
    projects,
    employers,
    enterprise,
}) => [
        [
            "01",
            "Plan",
            "Career Mapping",
            `Individual guidance helps students identify suitable pathways across ${discipline}, aligned with their interests and professional goals.`,
        ],
        [
            "02",
            "Prepare",
            "Professional Skill Development",
            `Structured preparation strengthens ${skills}, communication, digital capability and problem-solving ability.`,
        ],
        [
            "03",
            "Experience",
            "Internships & Live Projects",
            `${projects} give students practical exposure to professional tools, processes and workplace expectations.`,
        ],
        [
            "04",
            "Perform",
            "Placement Preparation",
            "Resume development, aptitude training, interviews, group discussions and mock recruitment activities build professional confidence.",
        ],
        [
            "05",
            "Connect",
            "Industry Interaction",
            `Expert sessions, visits and engagement with ${employers} connect academic learning with professional practice.`,
        ],
        [
            "06",
            "Create",
            "Innovation & Entrepreneurship",
            `Students receive support to explore ${enterprise}, independent practice and entrepreneurial opportunities.`,
        ],
    ];

const careerContentBySchool = {
    "commerce": {
        name: "Commerce",
        discipline: "accounting, finance, banking, taxation and enterprise",
        skills: "financial analysis, accounting tools and commercial judgement",
        projects:
            "commercial assignments, internships and finance-oriented projects",
        employers: "banks, consultancies, accounting firms and enterprises",
        enterprise: "advisory services, fintech ideas and commerce ventures",
        pathways: [
            [
                "AC",
                "Accounting & Audit",
                [
                    "Accountant",
                    "Audit Associate",
                    "Accounts Executive",
                    "Compliance Assistant",
                ],
            ],
            [
                "BF",
                "Banking & Finance",
                [
                    "Banking Associate",
                    "Credit Analyst",
                    "Financial Analyst",
                    "Insurance Executive",
                ],
            ],
            [
                "TX",
                "Taxation & Compliance",
                [
                    "Tax Assistant",
                    "GST Executive",
                    "Compliance Analyst",
                    "Payroll Executive",
                ],
            ],
            [
                "EN",
                "Enterprise & Consulting",
                [
                    "Business Consultant",
                    "Operations Executive",
                    "Entrepreneur",
                    "Commerce Analyst",
                ],
            ],
        ],
    },
    "hotelManagement": {
        name: "Hotel Management",
        discipline:
            "hotels, culinary operations, tourism, events and guest experience",
        skills:
            "service standards, operational coordination and hospitality communication",
        projects:
            "industrial training, live service assignments and hospitality internships",
        employers:
            "hotels, resorts, restaurants, travel companies and event organisations",
        enterprise: "food ventures, travel services and hospitality concepts",
        pathways: [
            [
                "HO",
                "Hotel Operations",
                [
                    "Front Office Executive",
                    "Guest Relations Executive",
                    "Housekeeping Supervisor",
                    "Duty Manager",
                ],
            ],
            [
                "FB",
                "Food & Beverage",
                [
                    "Restaurant Executive",
                    "Banquet Coordinator",
                    "Service Supervisor",
                    "Bar Operations Executive",
                ],
            ],
            [
                "CU",
                "Culinary Careers",
                [
                    "Commis Chef",
                    "Bakery Associate",
                    "Kitchen Executive",
                    "Culinary Entrepreneur",
                ],
            ],
            [
                "TE",
                "Tourism & Events",
                [
                    "Travel Executive",
                    "Event Coordinator",
                    "Tour Operations Executive",
                    "Cruise Hospitality Associate",
                ],
            ],
        ],
    },
    "science": {
        name: "Science",
        discipline:
            "laboratories, scientific industries, analytics and higher research",
        skills:
            "laboratory methods, quantitative reasoning and scientific communication",
        projects: "laboratory internships, field studies and research assignments",
        employers:
            "research laboratories, testing organisations and scientific industries",
        enterprise: "scientific services, testing solutions and research-led ideas",
        pathways: [
            [
                "RL",
                "Research Laboratories",
                [
                    "Research Assistant",
                    "Laboratory Analyst",
                    "Project Assistant",
                    "Scientific Officer",
                ],
            ],
            [
                "QT",
                "Quality & Testing",
                [
                    "Quality Analyst",
                    "Testing Executive",
                    "Validation Assistant",
                    "Documentation Executive",
                ],
            ],
            [
                "DA",
                "Data & Analysis",
                [
                    "Scientific Data Analyst",
                    "Statistical Assistant",
                    "Research Data Coordinator",
                    "Technical Analyst",
                ],
            ],
            [
                "HS",
                "Higher Studies",
                [
                    "Postgraduate Researcher",
                    "Teaching Assistant",
                    "Academic Researcher",
                    "Competitive Examination Aspirant",
                ],
            ],
        ],
    },
    "artsHumanities": {
        name: "Arts & Humanities",
        discipline:
            "education, writing, public service, research and social impact",
        skills:
            "critical interpretation, writing, research and public communication",
        projects: "field studies, editorial projects and social-sector internships",
        employers:
            "schools, publishers, cultural organisations and development institutions",
        enterprise:
            "content practices, cultural initiatives and independent research",
        pathways: [
            [
                "EW",
                "Education & Writing",
                [
                    "Content Writer",
                    "Editor",
                    "Academic Coordinator",
                    "Language Trainer",
                ],
            ],
            [
                "CS",
                "Civil & Public Services",
                [
                    "Civil Services Aspirant",
                    "Programme Associate",
                    "Policy Assistant",
                    "Public Affairs Executive",
                ],
            ],
            [
                "SR",
                "Social Research",
                [
                    "Research Assistant",
                    "Survey Coordinator",
                    "Community Researcher",
                    "Documentation Officer",
                ],
            ],
            [
                "CC",
                "Culture & Communication",
                [
                    "Cultural Programme Executive",
                    "Communications Associate",
                    "Museum Assistant",
                    "Publishing Executive",
                ],
            ],
        ],
    },
    "law": {
        name: "Law",
        discipline:
            "litigation, corporate practice, compliance, policy and judicial services",
        skills: "legal research, drafting, advocacy and ethical reasoning",
        projects: "moots, legal clinics, internships and case-based assignments",
        employers: "law firms, courts, companies and public institutions",
        enterprise:
            "legal advisory practices, policy initiatives and justice-focused ventures",
        pathways: [
            [
                "LT",
                "Litigation",
                [
                    "Legal Associate",
                    "Litigation Assistant",
                    "Advocacy Intern",
                    "Court Clerk",
                ],
            ],
            [
                "CL",
                "Corporate Law",
                [
                    "Corporate Legal Associate",
                    "Contract Executive",
                    "Legal Operations Analyst",
                    "Due Diligence Assistant",
                ],
            ],
            [
                "PC",
                "Policy & Compliance",
                [
                    "Compliance Analyst",
                    "Policy Researcher",
                    "Regulatory Executive",
                    "Risk Associate",
                ],
            ],
            [
                "JP",
                "Judicial Preparation",
                [
                    "Judicial Services Aspirant",
                    "Legal Researcher",
                    "Academic Assistant",
                    "Public Prosecutor Aspirant",
                ],
            ],
        ],
    },
    "pharmacy": {
        name: "Pharmacy",
        discipline:
            "production, quality assurance, clinical services and pharmaceutical research",
        skills:
            "formulation knowledge, laboratory discipline and regulatory documentation",
        projects:
            "pharmacy internships, formulation projects and quality assignments",
        employers:
            "pharmaceutical manufacturers, laboratories and healthcare organisations",
        enterprise: "pharmacy services, healthcare products and formulation ideas",
        pathways: [
            [
                "PD",
                "Production",
                [
                    "Production Pharmacist",
                    "Manufacturing Executive",
                    "Packaging Executive",
                    "Process Associate",
                ],
            ],
            [
                "QA",
                "Quality",
                [
                    "Quality Assurance Executive",
                    "Quality Control Analyst",
                    "Validation Associate",
                    "Documentation Officer",
                ],
            ],
            [
                "CR",
                "Clinical & Regulatory",
                [
                    "Clinical Research Associate",
                    "Regulatory Affairs Assistant",
                    "Pharmacovigilance Associate",
                    "Medical Coding Associate",
                ],
            ],
            [
                "RS",
                "Research & Services",
                [
                    "Research Assistant",
                    "Hospital Pharmacist",
                    "Community Pharmacist",
                    "Formulation Associate",
                ],
            ],
        ],
    },
    "design": {
        name: "Design",
        discipline:
            "studios, digital experience, creative enterprise and independent practice",
        skills: "portfolio presentation, design tools and client communication",
        projects: "studio briefs, portfolio projects and creative internships",
        employers:
            "design studios, agencies, product teams and creative organisations",
        enterprise:
            "independent studios, creative products and design-led ventures",
        pathways: [
            [
                "VD",
                "Visual Design",
                [
                    "Graphic Designer",
                    "Brand Designer",
                    "Illustrator",
                    "Visual Communication Designer",
                ],
            ],
            [
                "UX",
                "Digital Experience",
                [
                    "UI Designer",
                    "UX Research Assistant",
                    "Interaction Designer",
                    "Web Designer",
                ],
            ],
            [
                "PD",
                "Product & Spatial",
                [
                    "Product Design Assistant",
                    "Retail Designer",
                    "Exhibition Designer",
                    "CAD Visualiser",
                ],
            ],
            [
                "CE",
                "Creative Enterprise",
                [
                    "Freelance Designer",
                    "Studio Coordinator",
                    "Creative Entrepreneur",
                    "Design Consultant",
                ],
            ],
        ],
    },
    "education": {
        name: "Education",
        discipline:
            "school teaching, academic coordination, educational content and higher study",
        skills: "lesson planning, classroom communication and reflective practice",
        projects: "teaching practice, school internships and educational projects",
        employers:
            "schools, learning organisations and education-content companies",
        enterprise:
            "learning resources, tutoring services and education initiatives",
        pathways: [
            [
                "ST",
                "School Teaching",
                [
                    "Subject Teacher",
                    "Primary Educator",
                    "Teaching Assistant",
                    "Special Educator Assistant",
                ],
            ],
            [
                "AC",
                "Academic Coordination",
                [
                    "Academic Coordinator",
                    "School Administrator",
                    "Assessment Executive",
                    "Training Coordinator",
                ],
            ],
            [
                "EC",
                "Educational Content",
                [
                    "Curriculum Associate",
                    "Content Developer",
                    "Instructional Design Assistant",
                    "Education Editor",
                ],
            ],
            [
                "HS",
                "Higher Studies",
                [
                    "Postgraduate Student",
                    "Education Researcher",
                    "Teacher Eligibility Aspirant",
                    "Academic Assistant",
                ],
            ],
        ],
    },
    "journalismMassCommunication": {
        name: "Journalism & Mass Communication",
        discipline:
            "newsrooms, broadcasting, public relations and digital communication",
        skills:
            "reporting, verification, multimedia production and editorial judgement",
        projects: "newsroom simulations, field reporting and media internships",
        employers:
            "news organisations, broadcasters, agencies and digital publishers",
        enterprise: "independent media, podcasts and communication ventures",
        pathways: [
            [
                "JN",
                "Journalism",
                ["Reporter", "Sub-editor", "Correspondent", "News Researcher"],
            ],
            [
                "BM",
                "Broadcast Media",
                [
                    "Production Assistant",
                    "Video Journalist",
                    "Radio Producer",
                    "Anchor Trainee",
                ],
            ],
            [
                "PR",
                "PR & Corporate Communication",
                [
                    "PR Executive",
                    "Communications Associate",
                    "Media Relations Executive",
                    "Corporate Content Writer",
                ],
            ],
            [
                "DM",
                "Digital Media",
                [
                    "Digital Producer",
                    "Social Media Executive",
                    "Podcast Producer",
                    "Multimedia Editor",
                ],
            ],
        ],
    },
    "mediaEntertainment": {
        name: "Media & Entertainment",
        discipline:
            "film, television, performance, digital entertainment and creative production",
        skills: "story development, production tools and collaborative delivery",
        projects: "studio productions, live briefs and entertainment internships",
        employers:
            "production houses, studios, broadcasters and entertainment platforms",
        enterprise:
            "independent productions, creator brands and entertainment ventures",
        pathways: [
            [
                "FT",
                "Film & Television",
                [
                    "Production Assistant",
                    "Assistant Director",
                    "Camera Assistant",
                    "Script Coordinator",
                ],
            ],
            [
                "PP",
                "Post-production",
                [
                    "Video Editor",
                    "Motion Graphics Artist",
                    "Sound Assistant",
                    "Colour Assistant",
                ],
            ],
            [
                "DP",
                "Digital Production",
                [
                    "Content Producer",
                    "Channel Executive",
                    "Streaming Coordinator",
                    "Creative Strategist",
                ],
            ],
            [
                "PE",
                "Performance & Events",
                [
                    "Production Coordinator",
                    "Talent Coordinator",
                    "Event Producer",
                    "Stage Manager",
                ],
            ],
        ],
    },
    "libraryInformationScience": {
        name: "Library & Information Science",
        discipline:
            "academic libraries, digital repositories, archives and knowledge management",
        skills: "cataloguing, metadata, digital systems and user services",
        projects:
            "library internships, digitisation work and information-service projects",
        employers: "universities, libraries, archives and knowledge organisations",
        enterprise:
            "information services, digitisation support and knowledge consulting",
        pathways: [
            [
                "AL",
                "Academic Libraries",
                [
                    "Assistant Librarian",
                    "Library Assistant",
                    "Reference Assistant",
                    "Circulation Executive",
                ],
            ],
            [
                "DR",
                "Digital Repositories",
                [
                    "Digital Library Assistant",
                    "Repository Executive",
                    "Metadata Associate",
                    "Digitisation Assistant",
                ],
            ],
            [
                "AR",
                "Archives",
                [
                    "Archive Assistant",
                    "Records Executive",
                    "Preservation Assistant",
                    "Documentation Associate",
                ],
            ],
            [
                "KM",
                "Knowledge Management",
                [
                    "Information Executive",
                    "Research Support Assistant",
                    "Content Curator",
                    "Knowledge Coordinator",
                ],
            ],
        ],
    },
    "yogaNaturopathy": {
        name: "Yoga & Naturopathy",
        discipline:
            "wellness education, yoga instruction, preventive health and community wellbeing",
        skills: "safe instruction, wellness communication and responsible practice",
        projects: "supervised practice, wellness camps and community internships",
        employers:
            "wellness centres, educational institutions and community organisations",
        enterprise:
            "yoga studios, wellness programmes and responsible consultation",
        pathways: [
            [
                "YI",
                "Yoga Instruction",
                [
                    "Yoga Instructor",
                    "Group Practice Facilitator",
                    "School Yoga Trainer",
                    "Corporate Wellness Trainer",
                ],
            ],
            [
                "WE",
                "Wellness Education",
                [
                    "Wellness Educator",
                    "Lifestyle Programme Assistant",
                    "Health Workshop Facilitator",
                    "Content Educator",
                ],
            ],
            [
                "CW",
                "Community Wellbeing",
                [
                    "Community Wellness Associate",
                    "Camp Coordinator",
                    "Outreach Facilitator",
                    "Preventive Health Assistant",
                ],
            ],
            [
                "EP",
                "Enterprise & Progression",
                [
                    "Studio Entrepreneur",
                    "Wellness Programme Founder",
                    "Higher Studies Aspirant",
                    "Research Assistant",
                ],
            ],
        ],
    },
    "lifeScience": {
        name: "Life Science",
        discipline:
            "biotechnology, research laboratories, environmental science and advanced study",
        skills:
            "biological methods, data interpretation and research communication",
        projects:
            "laboratory research, field investigation and biotechnology internships",
        employers:
            "biotechnology companies, laboratories and environmental organisations",
        enterprise:
            "bio-solutions, testing services and sustainability initiatives",
        pathways: [
            [
                "BT",
                "Biotechnology",
                [
                    "Biotechnology Associate",
                    "Production Assistant",
                    "Bioprocess Trainee",
                    "Application Assistant",
                ],
            ],
            [
                "RL",
                "Research Laboratories",
                [
                    "Research Assistant",
                    "Laboratory Analyst",
                    "Microbiology Assistant",
                    "Molecular Biology Trainee",
                ],
            ],
            [
                "ES",
                "Environment",
                [
                    "Environmental Analyst",
                    "Field Research Assistant",
                    "Sustainability Associate",
                    "Ecology Project Assistant",
                ],
            ],
            [
                "AS",
                "Advanced Study",
                [
                    "Postgraduate Researcher",
                    "Academic Assistant",
                    "Clinical Research Trainee",
                    "Competitive Examination Aspirant",
                ],
            ],
        ],
    },
    "vocationalManagement": {
        name: "Vocational Management",
        discipline:
            "skilled employment, supervision, self-employment and further certification",
        skills:
            "role-specific tools, workplace communication and operational discipline",
        projects: "apprenticeships, workplace projects and industry training",
        employers:
            "service organisations, manufacturing units and local enterprises",
        enterprise:
            "self-employment, trade services and small-business opportunities",
        pathways: [
            [
                "SE",
                "Skilled Employment",
                [
                    "Operations Associate",
                    "Service Technician",
                    "Process Assistant",
                    "Field Executive",
                ],
            ],
            [
                "SV",
                "Supervision",
                [
                    "Team Supervisor",
                    "Shift Coordinator",
                    "Quality Assistant",
                    "Site Coordinator",
                ],
            ],
            [
                "EN",
                "Enterprise",
                [
                    "Self-employed Professional",
                    "Service Entrepreneur",
                    "Freelance Operator",
                    "Micro-business Owner",
                ],
            ],
            [
                "FC",
                "Further Certification",
                [
                    "Apprentice",
                    "Certification Candidate",
                    "Advanced Skills Trainee",
                    "Industry Licence Aspirant",
                ],
            ],
        ],
    },
    "healthAlliedScience": {
        name: "Health & Allied Science",
        discipline:
            "diagnostic services, clinical support, rehabilitation and community health",
        skills:
            "clinical procedures, patient communication and healthcare documentation",
        projects:
            "clinical observation, supervised practice and healthcare internships",
        employers:
            "hospitals, diagnostic centres and community-health organisations",
        enterprise:
            "health-support services, wellness programmes and clinical innovations",
        pathways: [
            [
                "DS",
                "Diagnostic Services",
                [
                    "Laboratory Technician",
                    "Imaging Assistant",
                    "Diagnostic Associate",
                    "Sample Collection Executive",
                ],
            ],
            [
                "CS",
                "Clinical Support",
                [
                    "Clinical Assistant",
                    "Patient Care Associate",
                    "OT Assistant",
                    "Healthcare Coordinator",
                ],
            ],
            [
                "RH",
                "Rehabilitation",
                [
                    "Therapy Assistant",
                    "Rehabilitation Associate",
                    "Fitness Support Executive",
                    "Assistive Care Coordinator",
                ],
            ],
            [
                "CH",
                "Community Health",
                [
                    "Community Health Worker",
                    "Public Health Assistant",
                    "Outreach Coordinator",
                    "Health Educator",
                ],
            ],
        ],
    },
};

Object.values(careerContentBySchool).forEach((content) => {
    content.stages = buildCareerStages(content);
});

const schoolCareerStats = (content) => [
    ["45 LPA", "Highest offer", "University-wide indicator"],
    ["25 LPA", "Average offer", "Across placement activity"],
    ["100%", "Career assistance", "Structured student support"],
    [
        String(content.pathways.length),
        `${content.name} domains`,
        "Representative pathways",
    ],
];
const CareerHeading = ({
    content,
    dark = false,
    accent = "professional momentum.",
}) => (
    <header className="grid gap-8 lg:grid-cols-[1fr_.62fr] lg:items-end">
        <div>
            <div
                className={`flex items-center gap-4 text-sm font-bold uppercase tracking-[.16em] ${dark ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}
            >
                <span className="h-px w-10 bg-current" />
                Career services & placements
            </div>
            <h2
                className={`mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] ${dark ? "text-white" : "text-[#071A2F]"}`}
            >
                From academic capability to{" "}
                <em className={dark ? "text-[#D5AD57]" : "text-[#8B1E3F]"}>{accent}</em>
            </h2>
        </div>
        <p
            className={`text-base leading-8 ${dark ? "text-white/60" : "text-[#132337]/65"}`}
        >
            Career preparation across {content.discipline}, supported by professional
            mentoring, experience and recruitment readiness.
        </p>
    </header>
);
const StatStrip = ({ content, dark = false, className = "" }) => (
    <div className={`grid sm:grid-cols-2 xl:grid-cols-4 ${className}`}>
        {schoolCareerStats(content).map(([v, l, n], i) => (
            <article
                key={l}
                className={`min-h-[175px] p-6 ${dark ? "border border-white/15 bg-white/[.04] text-white" : "border border-[#071A2F]/10 bg-white text-[#071A2F]"}`}
            >
                <span
                    className={`text-sm font-bold ${dark ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}
                >
                    0{i + 1}
                </span>
                <strong
                    className={`mt-5 block font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal ${dark ? "text-[#D5AD57]" : "text-[#071A2F]"}`}
                >
                    {v}
                </strong>
                <h3 className="mt-3 text-lg font-semibold">{l}</h3>
                <p
                    className={`mt-2 text-sm leading-6 ${dark ? "text-white/50" : "text-[#132337]/55"}`}
                >
                    {n}
                </p>
            </article>
        ))}
    </div>
);
const PathwayGrid = ({ content, dark = false, shape = "rounded-[24px]" }) => (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {content.pathways.map(([code, title, roles]) => (
            <article
                key={code}
                className={`${shape} group border p-6 transition hover:-translate-y-1 ${dark ? "border-white/15 bg-white/[.04] text-white hover:border-[#D5AD57]" : "border-[#071A2F]/10 bg-white text-[#071A2F] hover:border-[#D5AD57]"}`}
            >
                <span
                    className={`grid h-12 w-12 place-items-center rounded-xl text-sm font-bold ${dark ? "bg-[#D5AD57] text-[#071A2F]" : "bg-[#071A2F] text-[#D5AD57]"}`}
                >
                    {code}
                </span>
                <h3 className="mt-6 text-xl font-semibold">{title}</h3>
                <ul
                    className={`mt-5 space-y-2 text-sm ${dark ? "text-white/55" : "text-[#132337]/60"}`}
                >
                    {roles.map((r) => (
                        <li key={r}>• {r}</li>
                    ))}
                </ul>
            </article>
        ))}
    </div>
);
const StageGrid = ({ content, dark = false, columns = "xl:grid-cols-3" }) => (
    <div className={`grid gap-4 md:grid-cols-2 ${columns}`}>
        {content.stages.map(([n, verb, title, text]) => (
            <article
                key={n}
                className={`group relative min-h-[270px] overflow-hidden border p-7 transition hover:-translate-y-1 ${dark ? "border-white/15 bg-white/[.04] text-white hover:border-[#D5AD57]" : "border-[#071A2F]/10 bg-white text-[#071A2F] hover:border-[#D5AD57]"}`}
            >
                <div className="flex justify-between">
                    <span className={dark ? "text-[#D5AD57]" : "text-[#8B1E3F]"}>
                        {n}
                    </span>
                    <span
                        className={`rounded-full px-4 py-2 text-sm font-bold ${dark ? "bg-white/10 text-[#D5AD57]" : "bg-[#F4E8EC] text-[#8B1E3F]"}`}
                    >
                        {verb}
                    </span>
                </div>
                <h3 className="mt-8 font-['Libre_Caslon_Display',Georgia,serif] text-2xl">
                    {title}
                </h3>
                <p
                    className={`mt-4 text-base leading-8 ${dark ? "text-white/55" : "text-[#132337]/60"}`}
                >
                    {text}
                </p>
                <span className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-[#8B1E3F] to-[#D5AD57] transition-all group-hover:w-full" />
            </article>
        ))}
    </div>
);

function CommerceCareerServicesPlacements() {
    const c = careerContentBySchool.commerce;

    return (
        <section className="bg-white py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} accent="commercial momentum." />
                <StatStrip content={c} className="mt-12 border-y-2 border-[#071A2F]" />
                <div className="mt-12 grid lg:grid-cols-[320px_1fr]">
                    <aside className="bg-[#071A2F] p-8 text-white">
                        <span className="text-sm font-bold text-[#D5AD57]">
                            MARKET REGISTER
                        </span>
                        <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Professional pathways with commercial relevance.
                        </h3>
                    </aside>
                    <div className="border border-[#071A2F]/10 p-6">
                        <PathwayGrid content={c} shape="rounded-none" />
                    </div>
                </div>
                <div className="mt-12">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function HotelCareerServicesPlacements() {
    const c = careerContentBySchool.hotelManagement;
    return (
        <section className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,rgba(139,30,63,.35),transparent_30%)]" />
            <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} dark accent="service excellence." />
                <StatStrip
                    content={c}
                    dark
                    className="mt-12 overflow-hidden rounded-[8px_34px_8px_8px]"
                />
                <div className="mt-14 grid gap-8 lg:grid-cols-[.40fr_1.28fr]">
                    <aside className="rounded-[8px_36px_8px_8px] bg-[#8B1E3F] p-8">
                        <span className="text-sm font-bold text-[#D5AD57]">
                            GLOBAL HOSPITALITY PATHWAYS
                        </span>
                        <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Careers built around people, service and memorable experiences.
                        </h3>
                    </aside>
                    <PathwayGrid content={c} dark shape="rounded-[28px_8px_28px_8px]" />
                </div>
                <div className="mt-14">
                    <StageGrid content={c} dark />
                </div>
            </div>
        </section>
    );
}
function ScienceCareerServicesPlacements() {
    const c = careerContentBySchool.science;
    return (
        <section className="bg-[#EEF4F8] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} accent="scientific direction." />
                <aside className="bg-[#071A2F] p-8 text-white mt-14">
                    <span className="text-sm font-bold text-[#D5AD57]">
                        SCIENCE CAREER OBSERVATORY
                    </span>
                    <div className="mt-8">
                        <StatStrip content={c} dark />
                    </div>
                </aside>
                <div className="mt-14">
                    <PathwayGrid content={c} shape="rounded-t-[70px]" />
                </div>
                <div className="mt-14 border-t-2 border-[#071A2F] pt-8">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function ArtsCareerServicesPlacements() {
    const c = careerContentBySchool.artsHumanities;
    return (
        <section className="bg-white py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <div className="border-y-2 border-[#071A2F] py-10">
                    <CareerHeading content={c} accent="human-centred careers." />
                </div>
                <StatStrip content={c} className="mt-10" />
                <div className="mt-14 grid gap-10 lg:grid-cols-[260px_1fr]">
                    <aside className="border-l-4 border-[#8B1E3F] bg-[#F4E8EC] p-7">
                        <span className="font-serif text-7xl text-[#8B1E3F]/15">“</span>
                        <p className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl text-[#071A2F]">
                            Insight, expression and cultural intelligence travel across
                            professions.
                        </p>
                    </aside>
                    <div>
                        <PathwayGrid content={c} shape="rounded-none" />
                    </div>
                </div>
                <div className="mt-14">
                    <StageGrid content={c} columns="xl:grid-cols-2" />
                </div>
            </div>
        </section>
    );
}
function LawCareerServicesPlacements() {
    const c = careerContentBySchool.law;
    return (
        <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <div className="border-t-8 border-[#8B1E3F] bg-white p-8 sm:p-10">
                    <CareerHeading content={c} accent="professional legal practice." />
                    <StatStrip content={c} className="mt-10" />
                </div>
                <div className="mt-10 grid lg:grid-cols-[280px_1fr]">
                    <aside className="bg-[#071A2F] p-8 text-white">
                        <span className="font-serif text-7xl text-[#D5AD57]">§</span>
                        <h3 className="mt-8 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            The legal career docket.
                        </h3>
                    </aside>
                    <PathwayGrid content={c} shape="rounded-none" />
                </div>
                <div className="mt-12">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function PharmacyCareerServicesPlacements() {
    const c = careerContentBySchool.pharmacy;
    return (
        <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} accent="pharmaceutical careers." />
                <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_340px]">
                    <StatStrip content={c} />
                    <aside className="rounded-[34px] bg-[#8B1E3F] p-8 text-white">
                        <span className="text-sm font-bold text-[#D5AD57]">
                            PROFESSIONAL FORMULATION
                        </span>
                        <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Science, quality and responsibility in every pathway.
                        </h3>
                    </aside>
                </div>
                <div className="mt-14">
                    <PathwayGrid content={c} shape="rounded-[32px]" />
                </div>
                <div className="mt-14">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function DesignCareerServicesPlacements() {
    const c = careerContentBySchool.design;
    return (
        <section className="bg-white py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <div className="grid gap-4 lg:grid-cols-12">
                    <div className="rounded-[34px] bg-[#D5AD57] p-8 lg:col-span-8">
                        <CareerHeading content={c} accent="creative practice." />
                    </div>
                    <div className="flex items-end rounded-[34px] bg-[#071A2F] p-8 text-white lg:col-span-4">
                        <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Portfolio. Practice. Professional identity.
                        </h3>
                    </div>
                </div>
                <StatStrip content={c} className="mt-6" />
                <div className="mt-12">
                    <PathwayGrid content={c} />
                </div>
                <div className="mt-12">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function EducationCareerServicesPlacements() {
    const content = careerContentBySchool.education;
    const stats = schoolCareerStats(content);

    return (
        <section
            className="relative overflow-hidden bg-[#EEF4F8] py-20 sm:py-24 lg:py-28"
            aria-labelledby="education-careers-heading"
        >
            {/* Background details */}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -right-36 top-16 h-96 w-96 rounded-full border-[70px] border-[#8B1E3F]/[0.035]" />
                <div className="absolute -left-32 bottom-20 h-80 w-80 rounded-full bg-[#D5AD57]/10 blur-[100px]" />
                <div className="absolute inset-0 opacity-[0.025] [background-image:linear-gradient(#071A2F_1px,transparent_1px)] [background-size:100%_44px]" />
            </div>

            <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                {/* Introduction */}
                <header className="grid gap-10 border-b-2 border-[#071A2F] pb-11 lg:grid-cols-[1fr_0.55fr] lg:items-end">
                    <div>
                        <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                            <span className="h-px w-11 bg-current" />
                            Career services & placements
                        </div>

                        <h2
                            id="education-careers-heading"
                            className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,72px)] font-normal leading-[1.04] tracking-[-0.04em] text-[#071A2F]"
                        >
                            Preparing educators for{" "}
                            <em className="font-normal text-[#8B1E3F]">
                                classroom confidence.
                            </em>
                        </h2>
                    </div>

                    <p className="border-l border-[#071A2F]/15 pl-7 text-base leading-8 text-[#132337]/65 max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                        Career preparation across school teaching, academic coordination,
                        educational content and higher study—supported by mentoring,
                        teaching practice and professional readiness.
                    </p>
                </header>

                {/* Career indicators */}
                <div className="mt-12 grid overflow-hidden border border-[#071A2F]/15 bg-white sm:grid-cols-2 lg:grid-cols-4">
                    {stats.map(([value, label, note], index) => (
                        <article
                            key={label}
                            className="group relative min-h-[190px] border-b border-[#071A2F]/10 p-6 transition duration-300 hover:bg-[#F4E8EC] sm:border-r lg:border-b-0 lg:last:border-r-0"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-[#8B1E3F]">
                                    0{index + 1}
                                </span>

                                <span className="grid h-9 w-9 place-items-center rounded-full border border-[#071A2F]/10 text-[#A77B2D]">
                                    ✓
                                </span>
                            </div>

                            <strong className="mt-6 block font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal text-[#071A2F]">
                                {value}
                            </strong>

                            <h3 className="mt-3 text-lg font-semibold text-[#071A2F]">
                                {label}
                            </h3>

                            <p className="mt-2 text-sm leading-6 text-[#132337]/55">{note}</p>

                            <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-[#8B1E3F] transition-transform duration-300 group-hover:scale-x-100" />
                        </article>
                    ))}
                </div>

                {/* Professional pathways */}
                <div className="mt-14 grid overflow-hidden border border-[#071A2F]/15 bg-white lg:grid-cols-[320px_1fr]">
                    <aside className="relative flex min-h-[520px] flex-col overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10">
                        <div
                            className="absolute inset-0 opacity-[0.055] [background-image:linear-gradient(white_1px,transparent_1px)] [background-size:100%_42px]"
                            aria-hidden="true"
                        />

                        <div className="relative">
                            <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#D5AD57]">
                                Professional pathways
                            </span>

                            <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.15] sm:text-5xl">
                                Careers that shape meaningful learning.
                            </h3>

                            <p className="mt-6 text-base leading-8 text-white/60">
                                Explore teaching, academic leadership, learning-content and
                                research pathways supported by structured career preparation.
                            </p>
                        </div>

                        <div className="relative mt-auto border-t border-white/15 pt-7">
                            <span className="text-sm font-bold uppercase tracking-[0.13em] text-[#D5AD57]">
                                Graduate direction
                            </span>

                            <p className="mt-3 text-xl font-semibold leading-8">
                                Teach. Guide. Create. Transform.
                            </p>
                        </div>
                    </aside>

                    <div className="grid sm:grid-cols-2">
                        {content.pathways.map(([code, title, roles], index) => (
                            <article
                                key={code}
                                className="group relative min-h-[260px] border-b border-[#071A2F]/10 p-6 transition duration-300 hover:bg-[#F7F2E8] sm:border-r sm:p-7 sm:[&:nth-child(even)]:border-r-0"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="grid h-12 min-w-12 place-items-center rounded-full bg-[#8B1E3F] px-3 text-sm font-bold text-white transition group-hover:bg-[#071A2F]">
                                        {code}
                                    </span>

                                    <span className="font-['Libre_Caslon_Display',Georgia,serif] text-5xl text-[#071A2F]/[0.05]">
                                        0{index + 1}
                                    </span>
                                </div>

                                <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">
                                    {title}
                                </h3>

                                <ul className="mt-5 grid gap-2 text-sm leading-6 text-[#132337]/60">
                                    {roles.map((role) => (
                                        <li key={role} className="flex items-start gap-3">
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D5AD57]" />
                                            {role}
                                        </li>
                                    ))}
                                </ul>

                                <span className="absolute bottom-0 left-0 h-1 w-0 bg-[#D5AD57] transition-all duration-300 group-hover:w-full" />
                            </article>
                        ))}
                    </div>
                </div>

                {/* Career preparation journey */}
                <div className="mt-16">
                    <div className="grid gap-8 lg:grid-cols-[0.65fr_1fr] lg:items-end">
                        <div>
                            <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                Teacher career development
                            </span>

                            <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(34px,4vw,54px)] font-normal leading-[1.08] text-[#071A2F]">
                                A guided journey from preparation to professional practice.
                            </h3>
                        </div>

                        <p className="text-base leading-8 text-[#132337]/60">
                            Each stage strengthens professional identity, teaching capability,
                            practical exposure and confidence for education-sector
                            opportunities.
                        </p>
                    </div>

                    <div className="relative mt-10">
                        <div
                            className="absolute bottom-0 left-[35px] top-0 hidden w-px bg-[#D5AD57] md:block"
                            aria-hidden="true"
                        />

                        <div className="space-y-4">
                            {content.stages.map(([number, verb, title, text], index) => (
                                <article
                                    key={number}
                                    className="group relative grid overflow-hidden border border-[#071A2F]/10 bg-white transition duration-300 hover:border-[#D5AD57] hover:shadow-[0_20px_55px_rgba(7,26,47,0.08)] md:grid-cols-[70px_150px_240px_1fr]"
                                >
                                    <div className="relative z-10 grid min-h-20 place-items-center bg-[#071A2F] p-4 text-sm font-bold text-[#D5AD57] transition group-hover:bg-[#8B1E3F] group-hover:text-white">
                                        {number}
                                    </div>

                                    <div className="flex items-center border-b border-[#071A2F]/10 px-5 py-5 md:border-b-0 md:border-r">
                                        <span className="rounded-full bg-[#F4E8EC] px-4 py-2 text-sm font-bold text-[#8B1E3F]">
                                            {verb}
                                        </span>
                                    </div>

                                    <div className="flex items-center border-b border-[#071A2F]/10 px-5 py-5 md:border-b-0 md:border-r">
                                        <h4 className="font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal leading-7 text-[#071A2F]">
                                            {title}
                                        </h4>
                                    </div>

                                    <div className="flex items-center px-5 py-5 md:px-7">
                                        <p className="text-sm leading-7 text-[#132337]/60">
                                            {text}
                                        </p>

                                        <span className="ml-auto hidden pl-6 text-xl text-[#A77B2D] transition-transform duration-300 group-hover:translate-x-1 lg:block">
                                            →
                                        </span>
                                    </div>

                                    {index === content.stages.length - 1 && (
                                        <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#8B1E3F] to-[#D5AD57]" />
                                    )}
                                </article>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Closing statement */}
                <div className="mt-12 grid overflow-hidden rounded-[8px_30px_8px_8px] bg-[#8B1E3F] text-white lg:grid-cols-[1fr_auto] lg:items-center">
                    <div className="p-7 sm:p-9">
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#D5AD57]">
                            The educator promise
                        </span>

                        <p className="mt-3 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-9 sm:text-3xl">
                            Reflective educators prepared to lead learning with knowledge,
                            empathy and confidence.
                        </p>
                    </div>

                    <a
                        href="#academic-programs"
                        className="flex min-h-24 items-center justify-center gap-4 border-t border-white/15 bg-[#D5AD57] px-8 text-sm font-bold uppercase tracking-[0.08em] text-[#071A2F] transition hover:bg-white lg:min-h-full lg:min-w-[250px] lg:border-l lg:border-t-0"
                    >
                        Explore programmes
                        <span aria-hidden="true">↗</span>
                    </a>
                </div>
            </div>
        </section>
    );
}
function JournalismCareerServicesPlacements() {
    const c = careerContentBySchool.journalismMassCommunication;
    return (
        <section className="bg-white py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <div className="border-y-4 border-[#071A2F] py-8">
                    <div className="mb-6 flex justify-between text-sm font-bold uppercase tracking-[.15em] text-[#8B1E3F]">
                        <span>Career newsroom</span>
                        <span>Graduate edition</span>
                    </div>
                    <CareerHeading content={c} accent="credible media careers." />
                </div>
                <StatStrip content={c} className="mt-8" />
                <div className="mt-12">
                    <PathwayGrid content={c} shape="rounded-none" />
                </div>
                <div className="mt-12">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function MediaCareerServicesPlacements() {
    const c = careerContentBySchool.mediaEntertainment;
    return (
        <section className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(139,30,63,.35),transparent_30%)]" />
            <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} dark accent="creative production." />
                <StatStrip content={c} dark className="mt-12" />
                <div className="mt-14">
                    <span className="text-sm font-bold text-[#D5AD57]">
                        PRODUCTION PATHWAYS
                    </span>
                    <div className="mt-6">
                        <PathwayGrid content={c} dark shape="rounded-[8px_30px_8px_8px]" />
                    </div>
                </div>
                <div className="mt-14">
                    <StageGrid content={c} dark />
                </div>
            </div>
        </section>
    );
}
function LibraryCareerServicesPlacements() {
    const c = careerContentBySchool.libraryInformationScience;
    return (
        <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <div className="grid border border-[#071A2F]/15 lg:grid-cols-[280px_1fr]">
                    <aside className="bg-[#071A2F] p-8 text-white">
                        <span className="text-sm font-bold text-[#D5AD57]">
                            CAREER CATALOGUE
                        </span>
                        <h3 className="mt-8 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Organise knowledge. Expand access.
                        </h3>
                    </aside>
                    <div className="p-8">
                        <CareerHeading content={c} accent="connected knowledge careers." />
                    </div>
                </div>
                <StatStrip content={c} className="mt-8" />
                <div className="mt-12">
                    <PathwayGrid content={c} shape="rounded-none" />
                </div>
                <div className="mt-12">
                    <StageGrid content={c} columns="xl:grid-cols-2" />
                </div>
            </div>
        </section>
    );
}
function YogaCareerServicesPlacements() {
    const content = careerContentBySchool.yogaNaturopathy;
    const stats = schoolCareerStats(content);

    return (
        <section
            className="relative overflow-hidden bg-[#F4E8EC] py-20 sm:py-24 lg:py-28"
            aria-labelledby="yoga-careers-heading"
        >
            {/* Decorative background */}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -left-40 top-28 h-[460px] w-[460px] rounded-full border border-[#8B1E3F]/10" />
                <div className="absolute -left-24 top-44 h-[330px] w-[330px] rounded-full border border-[#D5AD57]/25" />

                <div className="absolute -right-52 bottom-10 h-[520px] w-[520px] rounded-full bg-[#D5AD57]/10 blur-[110px]" />

                <div className="absolute inset-0 opacity-[0.025] [background-image:radial-gradient(#071A2F_1px,transparent_1px)] [background-size:32px_32px]" />
            </div>

            <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                {/* Introduction */}
                <header className="mx-auto max-w-5xl text-center">
                    <div className="flex items-center justify-center gap-4 text-sm font-bold uppercase tracking-[0.18em] text-[#8B1E3F]">
                        <span className="h-px w-10 bg-current" />
                        Career services & placements
                        <span className="h-px w-10 bg-current" />
                    </div>

                    <h2
                        id="yoga-careers-heading"
                        className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5.5vw,74px)] font-normal leading-[1.04] tracking-[-0.04em] text-[#071A2F]"
                    >
                        Transform disciplined practice into{" "}
                        <em className="font-normal text-[#8B1E3F]">
                            purposeful wellness careers.
                        </em>
                    </h2>

                    <p className="mx-auto mt-7 max-w-3xl text-base leading-8 text-[#132337]/65 sm:text-lg">
                        Career preparation across yoga instruction, wellness education,
                        community wellbeing and independent practice, supported by
                        professional mentoring and supervised experience.
                    </p>

                    <div className="relative mx-auto mt-10 grid h-24 w-24 place-items-center rounded-full border border-[#8B1E3F]/20">
                        <span className="absolute inset-3 rounded-full border border-[#D5AD57]/60" />
                        <span className="relative text-2xl text-[#8B1E3F]">✦</span>
                    </div>
                </header>

                {/* Career indicators */}
                <div className="mt-14 grid overflow-hidden rounded-[32px] border border-[#071A2F]/10 bg-white shadow-[0_24px_70px_rgba(7,26,47,0.07)] sm:grid-cols-2 lg:grid-cols-4">
                    {stats.map(([value, label, note], index) => (
                        <article
                            key={label}
                            className="group relative min-h-[190px] border-b border-[#071A2F]/10 p-6 text-center transition duration-300 hover:bg-[#F4E8EC] sm:border-r lg:border-b-0 lg:last:border-r-0"
                        >
                            <span className="mx-auto grid h-9 w-9 place-items-center rounded-full bg-[#F7F2E8] text-sm font-bold text-[#8B1E3F]">
                                0{index + 1}
                            </span>

                            <strong className="mt-5 block font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal text-[#071A2F]">
                                {value}
                            </strong>

                            <h3 className="mt-3 text-lg font-semibold text-[#071A2F]">
                                {label}
                            </h3>

                            <p className="mt-2 text-sm leading-6 text-[#132337]/55">{note}</p>

                            <span className="absolute inset-x-0 bottom-0 mx-auto h-1 w-0 bg-[#8B1E3F] transition-all duration-300 group-hover:w-full" />
                        </article>
                    ))}
                </div>

                {/* Professional pathways */}
                <div className="mt-16">
                    <div className="grid gap-8 border-b border-[#071A2F]/15 pb-9 lg:grid-cols-[1fr_0.6fr] lg:items-end">
                        <div>
                            <span className="text-sm font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                Professional pathways
                            </span>

                            <h3 className="mt-4 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4.5vw,58px)] font-normal leading-[1.08] text-[#071A2F]">
                                Careers grounded in awareness, wellbeing and{" "}
                                <em className="font-normal text-[#8B1E3F]">
                                    responsible guidance.
                                </em>
                            </h3>
                        </div>

                        <p className="text-base leading-8 text-[#132337]/60">
                            Explore pathways that connect traditional understanding,
                            professional communication and supervised wellness practice.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                        {content.pathways.map(([code, title, roles], index) => (
                            <article
                                key={code}
                                className="group relative flex min-h-[360px] flex-col overflow-hidden rounded-[52px_10px_52px_10px] border border-[#071A2F]/10 bg-white p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:shadow-[0_24px_60px_rgba(7,26,47,0.1)]"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="grid h-14 w-14 place-items-center rounded-full bg-[#071A2F] text-sm font-bold text-[#D5AD57] transition duration-300 group-hover:bg-[#8B1E3F] group-hover:text-white">
                                        {code}
                                    </span>

                                    <span className="font-['Libre_Caslon_Display',Georgia,serif] text-5xl text-[#071A2F]/[0.05]">
                                        0{index + 1}
                                    </span>
                                </div>

                                <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-8 text-[#071A2F]">
                                    {title}
                                </h3>

                                <ul className="mt-6 space-y-3">
                                    {roles.map((role) => (
                                        <li
                                            key={role}
                                            className="flex items-start gap-3 text-sm leading-6 text-[#132337]/60"
                                        >
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D5AD57]" />
                                            {role}
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto flex items-center gap-3 border-t border-[#071A2F]/10 pt-6 text-sm font-bold uppercase tracking-[0.1em] text-[#8B1E3F]">
                                    Career direction
                                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                                        →
                                    </span>
                                </div>

                                <span className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-[#8B1E3F] to-[#D5AD57] transition-all duration-500 group-hover:w-full" />
                            </article>
                        ))}
                    </div>
                </div>

                {/* Career preparation journey */}
                <div className="mt-20 overflow-hidden rounded-[10px_42px_10px_10px] bg-[#071A2F] text-white">
                    <div className="grid lg:grid-cols-[340px_1fr]">
                        <aside className="relative flex min-h-[520px] flex-col overflow-hidden border-b border-white/15 bg-[#8B1E3F] p-8 sm:p-10 lg:border-b-0 lg:border-r">
                            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full border border-white/10" />
                            <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full border border-[#D5AD57]/25" />

                            <div className="relative">
                                <span className="text-sm font-bold uppercase tracking-[0.16em] text-[#D5AD57]">
                                    Career development journey
                                </span>

                                <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.15]">
                                    Prepare with discipline. Progress with purpose.
                                </h3>

                                <p className="mt-6 text-base leading-8 text-white/65">
                                    A connected preparation model develops professional identity,
                                    practical confidence and responsible service.
                                </p>
                            </div>

                            <div className="relative mt-auto pt-12">
                                <div className="h-px bg-white/20" />

                                <p className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-9">
                                    Awareness becomes ability through consistent practice.
                                </p>
                            </div>
                        </aside>

                        <div className="grid md:grid-cols-2">
                            {content.stages.map(([number, verb, title, text], index) => (
                                <article
                                    key={number}
                                    className="group relative min-h-[280px] border-b border-white/12 p-7 transition duration-300 hover:bg-white/[0.06] sm:p-8 md:border-r md:[&:nth-child(even)]:border-r-0"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="grid h-11 w-11 place-items-center rounded-full border border-[#D5AD57]/35 text-sm font-bold text-[#D5AD57] transition group-hover:bg-[#D5AD57] group-hover:text-[#071A2F]">
                                            {number}
                                        </span>

                                        <span className="rounded-full bg-white/[0.07] px-4 py-2 text-sm font-bold text-white/70">
                                            {verb}
                                        </span>
                                    </div>

                                    <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-8 text-white">
                                        {title}
                                    </h3>

                                    <p className="mt-4 text-sm leading-7 text-white/55">{text}</p>

                                    <span className="absolute bottom-0 left-0 h-1 w-0 bg-[#D5AD57] transition-all duration-300 group-hover:w-full" />

                                    <span className="pointer-events-none absolute bottom-4 right-5 font-['Libre_Caslon_Display',Georgia,serif] text-6xl text-white/[0.025]">
                                        0{index + 1}
                                    </span>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Closing statement */}
                <div className="mt-10 flex flex-col gap-7 rounded-[28px] border border-[#071A2F]/10 bg-white p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-3xl">
                        <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#8B1E3F]">
                            The wellness professional promise
                        </span>

                        <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-9 text-[#071A2F] sm:text-3xl">
                            Informed practice. Empathetic guidance. Responsible wellbeing.
                        </p>
                    </div>

                    <a
                        href="#academic-programs"
                        className="inline-flex min-h-14 shrink-0 items-center justify-center gap-4 rounded-full bg-[#8B1E3F] px-7 text-sm font-bold uppercase tracking-[0.08em] text-white transition duration-300 hover:-translate-y-1 hover:bg-[#071A2F]"
                    >
                        Explore programmes
                        <span aria-hidden="true">↗</span>
                    </a>
                </div>
            </div>
        </section>
    );
}
function LifeCareerServicesPlacements() {
    const c = careerContentBySchool.lifeScience;
    return (
        <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} accent="living futures." />
                <div className="bg-[#071A2F] p-8 text-white my-14">
                    <span className="text-sm font-bold text-[#D5AD57]">
                        CAREER GENOME
                    </span>
                    <div className="mt-8">
                        <StatStrip content={c} dark />
                    </div>
                </div>
                <PathwayGrid content={c} shape="rounded-t-[70px]" />
                <div className="mt-14">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function VocationalCareerServicesPlacements() {
    const c = careerContentBySchool.vocationalManagement;
    return (
        <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <CareerHeading content={c} accent="workplace performance." />
                <StatStrip content={c} className="mt-12" />
                <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_320px]">
                    <div>
                        <PathwayGrid content={c} shape="rounded-none" />
                    </div>
                    <aside className="bg-[#8B1E3F] p-8 text-white">
                        <span className="text-sm font-bold text-[#D5AD57]">
                            CAREER READINESS STUDIO
                        </span>
                        <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Practical competence employers can recognise.
                        </h3>
                    </aside>
                </div>
                <div className="mt-14">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}
function HealthCareerServicesPlacements() {
    const c = careerContentBySchool.healthAlliedScience;
    return (
        <section className="bg-[#EEF4F8] py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                <div className="overflow-hidden rounded-[34px] bg-white shadow-xl">
                    <div className="h-2 bg-gradient-to-r from-[#071A2F] via-[#8B1E3F] to-[#D5AD57]" />
                    <div className="p-8 sm:p-10">
                        <CareerHeading
                            content={c}
                            accent="responsible healthcare careers."
                        />
                        <StatStrip content={c} className="mt-10" />
                    </div>
                </div>
                <div className="mt-12 grid gap-8 lg:grid-cols-[300px_1fr]">
                    <aside className="bg-[#071A2F] p-8 text-white">
                        <span className="grid h-16 w-16 place-items-center rounded-full border border-[#D5AD57] text-3xl text-[#D5AD57]">
                            +
                        </span>
                        <h3 className="mt-8 font-['Libre_Caslon_Display',Georgia,serif] text-4xl">
                            Clinical career preparation.
                        </h3>
                    </aside>
                    <PathwayGrid content={c} shape="rounded-[26px]" />
                </div>
                <div className="mt-14">
                    <StageGrid content={c} />
                </div>
            </div>
        </section>
    );
}

export const careerServicesPlacementComponents = {
    "school-of-management": ManagementCareerServicesPlacements,
    "school-of-commerce": CommerceCareerServicesPlacements,
    "school-of-engineering-and-technology":
        EngineeringTechnologyCareerServicesPlacements,
    "school-of-information-and-technology":
        InformationTechnologyCareerServicesPlacements,
    "school-of-hotel-management": HotelCareerServicesPlacements,
    "school-of-science": ScienceCareerServicesPlacements,
    "school-of-arts-and-humanities": ArtsCareerServicesPlacements,
    "school-of-law": LawCareerServicesPlacements,
    "school-of-pharmacy": PharmacyCareerServicesPlacements,
    "school-of-design": DesignCareerServicesPlacements,
    "school-of-education": EducationCareerServicesPlacements,
    "school-of-journalism-and-mass-communication":
        JournalismCareerServicesPlacements,
    "school-of-media-and-entertainment": MediaCareerServicesPlacements,
    "school-of-library-and-information-science": LibraryCareerServicesPlacements,
    "school-of-yoga-and-naturopathy": YogaCareerServicesPlacements,
    "school-of-life-science": LifeCareerServicesPlacements,
    "school-of-vocational-management": VocationalCareerServicesPlacements,
    "school-of-health-and-allied-science": HealthCareerServicesPlacements,
};
export function CareerServicesPlacementsRenderer({ school, ...props }) {
    const Component = useMemo(
        () =>
            careerServicesPlacementComponents[school] ||
            ScienceCareerServicesPlacements,
        [school],
    );
    return <Component {...props} />;
}
export { careerContentBySchool as careerServicesContent };
